import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { Form, Button, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { colors, constants } from "utils";
import { trackClickedButton } from "utils/analytics";
import { SIDEBAR_BUTTONS, sidebarGradeValue, useSidebarTracking } from "hooks/useSidebarTracking";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { useStandards } from "hooks/useStandards/useStandards";
import { StandardsFallback } from "components/shared/StandardsFallback/StandardsFallback";
import { useSemanticStandardSearch } from "hooks/useSemanticStandardSearch";
import { getGradeLabel, gradeHasVisibleStandards, isAssignableTarget, standardsFrameworkId, expandedNodesForCode } from "utils/standards";
import { useNoQuestionTracks } from "hooks/useCurriculum/useCurriculum";
import { useEducatorAccount } from "hooks/useEducatorAccount/useEducatorAccount";
import { withPanelViewMemory } from "hooks/usePageViewTracker";
import { toTrackQuestionFilters, customizedTrackTypes } from "components/pages/LessonsStandards/questionFilters";
import { SearchBar } from "components/shared/SearchBar/SearchBar";
import { StandardNavigationTree } from "components/pages/GroupCodeStandardSearch/StandardNavigationTree";
import { StandardDetailView } from "components/pages/GroupCodeStandardSearch/StandardDetailView";
import { SearchResultsView } from "components/pages/GroupCodeStandardSearch/SearchResultsView";
import { AssignGroupsDistributionModal } from "components/pages/Educator/Dashboard/AssignGroupsDistributionModal";
import { AssignedCodesBanner } from "components/shared/AssignedCodesBanner/AssignedCodesBanner";
import "components/pages/GroupCodeStandardSearch/groupCodeStandardSearch.css";
import logo from "assets/OKO_Core_Logotype2.svg";
import arrow_left_circle from "assets/arrow-left-circle.svg";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: white;
`;

const LogoImg = styled.img`
  width: 196px;
  display: block;
  margin-bottom: 24px;
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const Sidebar = styled.div`
  width: 388px;
  display: flex;
  flex-direction: column;
  border-right: 2px solid ${colors.nightlyWoods900};
  background-color: white;
`;

const SidebarHeader = styled.div`
  padding: 32px 16px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReturnButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.blue500};
  font-weight: 600;
  font-size: 14px;
  padding: 0;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const HeaderLeft = styled.div`
  flex: 0 0 auto;
`;

const HeaderTitle = styled.h1`
  color: ${colors.milkGlass500};
  font-size: 40px;
  font-weight: 700;
  margin: 0;
  line-height: 44px;
`;

const HeaderSubtitle = styled.p`
  color: #7DB199;
  font-size: 16px;
  margin: 0;
`;

const MainScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const NoSelection = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${colors.milkGlass900};
  font-size: 14px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  padding: 16px 32px;
  border-top: 1px solid #EAEAFC;
  background-color: #FFFEFE;
  box-shadow: 0 -1px 1.5px rgba(0, 0, 0, 0.05);
`;

const SaveButton = styled(Button)`
  background-color: #605DEC;
  border: none;
  border-radius: 8px;
  color: #FAF7F2;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
  width: 180px;
  &:hover, &:focus, &:active {
    background-color: #4F4DD4 !important;
    border: none !important;
  }
  &:disabled {
    background-color: #D5D3CE;
    color: #FAF7F2;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #605DEC;
  border-radius: 8px;
  color: #605DEC;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  padding: 0 16px;
  width: 180px;
  &:hover, &:focus, &:active {
    background: #F4F4FD !important;
    color: #605DEC !important;
    border-color: #605DEC !important;
  }
`;

const findStandardByCode = (standards, code) => {
  if (!code) return null;
  for (const gradeLevel of standards) {
    for (const domain of gradeLevel.domains || []) {
      for (const cluster of domain.clusters || []) {
        for (const std of cluster.standards || []) {
          if (std.code === code) {
            return { ...std, grade: gradeLevel.grade, domain, cluster };
          }
        }
      }
    }
  }
  return null;
};

const ViewChangeStandardPage = () => {
  const history = useHistory();
  const trackSidebar = useSidebarTracking();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const classroomId = urlParams.get("classroomId");
  const currentStandardCodeFromUrl = urlParams.get("currentStandardCode");
  const classIdFromUrl = urlParams.get("classId");
  const assignmentIdFromUrl = urlParams.get("assignmentId");

  const standardsData = useStandards(true);
  const { activeSchool } = useEducatorAccount();
  // A state-framework school (its active school names a framework id) must
  // surface zero-mapped standards so the teacher can pick them and read the
  // zero-count warning; a Common Core school keeps the existing hidden rule.
  const showZeroCountStandards = standardsFrameworkId(activeSchool) !== null;

  const [bundle, setBundle] = useState(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(true);
  const [selectedStandard, setSelectedStandard] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedGrade, setSelectedGrade] = useState("all");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRelationships, setSelectedRelationships] = useState(null);
  // Advanced Selection — mirrored up from StandardDetailView's own
  // per-(track_type, standard_code) filter state; reset there whenever the selected
  // standard changes, which flows back here through this callback.
  const [filters, setFilters] = useState({});

  // Fetch relationships for the picked standard so we know whether the new
  // standard exposes prerequisites/extensions tracks.
  useEffect(() => {
    if (!selectedStandard?.code || selectedStandard.type) {
      setSelectedRelationships(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`standards/${selectedStandard.code}/relationships`, { method: "GET" });
        if (!cancelled) setSelectedRelationships(data);
      } catch (e) {
        if (!cancelled) setSelectedRelationships(null);
        logError("ViewChangeStandard: failed to load relationships", e, {
          category: LogCategory.ERROR,
          standardCode: selectedStandard.code,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [selectedStandard?.code, selectedStandard?.type]);
  const [error, setError] = useState(null);

  const {
    query: searchQuery,
    results: searchResults,
    isSearching,
    showResults: showSearchResults,
    handleInputChange: handleSearchInputChange,
    handleSubmit: handleSearchSubmit,
    clearSearch,
    inputRef: searchInputRef,
  } = useSemanticStandardSearch();

  const goBackToGroups = useCallback(() => {
    const path = constants.EDUCATOR_PATH;
    history.push(classIdFromUrl ? `${path}?classId=${classIdFromUrl}` : path);
  }, [history, classIdFromUrl]);

  // Load the bundle being changed so the header reflects truth, not URL params.
  useEffect(() => {
    if (!classroomId) {
      setIsLoadingAssignment(false);
      setError("Missing classroom id.");
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoadingAssignment(true);
      try {
        const wrapper = await request(`groups/classroom/${classroomId}/assignment`, { method: "GET" });
        if (cancelled) return;
        const bundles = wrapper?.assignments || [];
        const matched = assignmentIdFromUrl
          ? bundles.find((b) => b.assignment_id === assignmentIdFromUrl)
          : bundles[0];
        setBundle(matched || null);
      } catch (e) {
        if (!cancelled) {
          setError(e?.detail || e?.message || "Failed to load classroom assignment.");
          logError("Failed to load classroom assignment", e, {
            category: LogCategory.ERROR,
            classroomId,
            assignmentId: assignmentIdFromUrl,
          });
        }
      } finally {
        if (!cancelled) setIsLoadingAssignment(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [classroomId, assignmentIdFromUrl]);

  // Auto-select the current standard once standards data + bundle have loaded.
  const currentStandardCode = bundle?.primary_standard_code || currentStandardCodeFromUrl;
  useEffect(() => {
    if (!standardsData || standardsData.length === 0) return;
    if (!currentStandardCode) return;
    if (selectedStandard) return;
    const std = findStandardByCode(standardsData, currentStandardCode);
    if (std) {
      setSelectedStandard(std);
      // Found-in-tree expansion, so state codes expand their real path (the
      // old string parse assumed CCSS shapes).
      setExpandedNodes(expandedNodesForCode(standardsData, currentStandardCode));
      if (std.grade) setSelectedGrade(std.grade);
    }
  }, [standardsData, currentStandardCode, selectedStandard]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const handleSelectStandard = (entity, grade, domain, cluster) => {
    setSelectedStandard({ ...entity, grade, domain, cluster });
    // Selecting opens the node and its path, matching the lesson tree and the
    // quick-group picker — a clicked domain should reveal what is inside it.
    setExpandedNodes((prev) => ({
      ...prev,
      ...(grade ? { [grade]: true } : {}),
      ...(domain?.code ? { [domain.code]: true } : {}),
      ...(cluster?.code ? { [cluster.code]: true } : {}),
      ...(entity?.code ? { [entity.code]: true } : {}),
    }));
    if (showSearchResults) clearSearch();
  };

  const handleGradeChange = (e) => {
    const grade = e.target.value;
    trackSidebar(SIDEBAR_BUTTONS.GRADE_FILTER, { grade: sidebarGradeValue(grade) });
    setSelectedGrade(grade);
    if (grade !== "all") {
      setExpandedNodes(prev => ({ ...prev, [grade]: true }));
    }
  };

  // Available grades: same shared filter as GroupCodeStandardSearch (skip
  // empties; state trees also keep grades whose only standards are zero-mapped).
  const availableGrades = (standardsData || [])
    .filter(gradeLevel => gradeHasVisibleStandards(gradeLevel, showZeroCountStandards))
    .map(gradeLevel => gradeLevel.grade);

  const filteredStandards = selectedGrade === "all"
    ? (standardsData || [])
    : (standardsData || []).filter(g => g.grade === selectedGrade);

  // hasChanged: only enable Save when the educator picked a *different* valid
  // target. Standards, clusters and domains all qualify; whole grades don't. The
  // prereq/extension gating below needs no leaf guard — relationships come back
  // empty for a cluster or domain, so those tracks are already suppressed.
  const hasChanged = isAssignableTarget(selectedStandard)
    && selectedStandard.code !== currentStandardCode;

  const handleSaveClick = () => {
    if (!hasChanged) return;
    trackClickedButton("Save Standard", {
      page_name: "Change Standard",
      assignment_type: "Standard",
      class: classroomId || null,
      assignment_id: bundle?.assignment_id || null,
      previous_standard_id: currentStandardCode || null,
      new_standard_id: selectedStandard?.code || null,
    });
    setShowAssignModal(true);
  };

  // Change Lesson Focus edits an EXISTING bundle, so we commit via PUT (the
  // modal's edit path) rather than the pool-gated POST /groups/assign. One atomic
  // call swaps the bundle's primary standard, redistributes its codes to the chosen
  // counts, and attaches the Advanced Selection filters per track — mirroring the
  // lesson flow's single change-lesson call (requires the BE's
  // `distribution` param on this endpoint).
  // Specific prerequisite/extension standards are auto-picked server-side from the
  // new standard's relationships. Throws on failure so the modal surfaces the error
  // and stays open.
  const commitEdit = async (counts) => {
    if (!classroomId || !selectedStandard?.code || !bundle?.assignment_id) return;
    const questionFilters = toTrackQuestionFilters(filters);
    await request(
      `groups/classrooms/${classroomId}/assignments/${bundle.assignment_id}/standard`,
      {
        method: "PUT",
        payload: {
          new_standard_code: selectedStandard.code,
          distribution: counts,
          ...(questionFilters.length ? { question_filters: questionFilters } : {}),
        },
      },
    );
  };

  const handleModalSaved = () => {
    setShowAssignModal(false);
    goBackToGroups();
  };

  // Seed the modal's distribution from the bundle currently being edited.
  const initialCounts = useMemo(() => {
    const acc = { core: 0, prerequisites: 0, extensions: 0 };
    (bundle?.assignments || []).forEach((a) => {
      if (a.track_type && acc[a.track_type] !== undefined) acc[a.track_type] += 1;
    });
    return acc;
  }, [bundle]);

  const bundleCodeCount = (bundle?.assignments || []).length;

  const newStandardHasPrereqs = (selectedRelationships?.prerequisites || []).some(
    (group) => group.standards && group.standards.length > 0,
  );
  const newStandardHasExtensions = (selectedRelationships?.extensions || []).some(
    (group) => group.standards && group.standards.length > 0,
  );

  // Tracks whose backing standard(s) have no questions to assign — disabled in the
  // distribution modal so the educator can't send groups to an empty level. Computed
  // from the consensus question page (the same source StandardDetailView uses), not
  // the global tree count.
  const noQuestionTracks = useNoQuestionTracks({
    flow: "standard",
    coreCodes: selectedStandard?.code ? [selectedStandard.code] : [],
    prereqCodes: (selectedRelationships?.prerequisites || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
    extCodes: (selectedRelationships?.extensions || []).flatMap((g) => (g.standards || []).map((s) => s.code)),
  });

  const groupCodes = (bundle?.assignments || []).map(a => a.group_code);
  const currentStandardName = bundle?.primary_standard_name;
  const currentStandard = currentStandardCode
    ? findStandardByCode(standardsData || [], currentStandardCode)
    : null;
  const currentStandardDisplayName = currentStandardName || currentStandard?.name || "";

  const standardsLoading = !standardsData || standardsData.length === 0;

  return (
    <PageContainer>
      <Body>
        <Sidebar>
          <SidebarHeader>
            <LogoImg src={logo} alt="OKO" />
            <ReturnButton
              onClick={() => {
                trackSidebar(SIDEBAR_BUTTONS.BACK_TO_GROUPS);
                goBackToGroups();
              }}
            >
              <img src={arrow_left_circle} alt="Return" />
              Return to Groups
            </ReturnButton>
            <Form.Select value={selectedGrade} onChange={handleGradeChange} aria-label="Filter standards by grade">
              <option value="all">All Grades</option>
              {availableGrades.map(grade => (
                <option key={grade} value={grade}>{getGradeLabel(grade)}</option>
              ))}
            </Form.Select>
          </SidebarHeader>
          <SidebarContent>
            {standardsLoading ? (
              <div style={{ padding: 32, textAlign: "center" }}>
                <StandardsFallback size="2x" />
              </div>
            ) : (
              <StandardNavigationTree
                standards={filteredStandards}
                selectedStandard={selectedStandard}
                expandedNodes={expandedNodes}
                onToggleNode={toggleNode}
                onSelectStandard={handleSelectStandard}
                showZeroCountStandards={showZeroCountStandards}
              />
            )}
          </SidebarContent>
        </Sidebar>

        <Main>
          <ContentHeader>
            <HeaderLeft>
              <HeaderTitle>{constants.VIEW_CHANGE_STANDARD}</HeaderTitle>
              <HeaderSubtitle>
                Choose a standard from the left to update the current group assignment.
              </HeaderSubtitle>
            </HeaderLeft>
            <SearchBar
              ref={searchInputRef}
              value={searchQuery}
              onChange={handleSearchInputChange}
              onSubmit={handleSearchSubmit}
              placeholder={"Try: “Multiplying Fractions” or “Proportional relationships”"}
              ariaLabel="Search standards"
              isLoading={isSearching}
            />
          </ContentHeader>

          {(groupCodes.length > 0 || currentStandardCode) && (
            <AssignedCodesBanner
              groupCodes={groupCodes}
              label="Current Standard"
              value={currentStandardCode
                ? `${currentStandardCode}${currentStandardDisplayName ? ` ${currentStandardDisplayName}` : ""}`
                : null}
            />
          )}

          <MainScroll>
            {isLoadingAssignment && !bundle ? (
              <div style={{ padding: 48, textAlign: "center" }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
            ) : showSearchResults ? (
              <SearchResultsView
                searchResults={searchResults || []}
                onResultClick={handleSelectStandard}
                onBackClick={clearSearch}
                standardsData={standardsData}
                showZeroCountStandards={showZeroCountStandards}
                activeSchool={activeSchool}
              />
            ) : selectedStandard ? (
              <StandardDetailView
                standard={selectedStandard}
                groupCode={null}
                onStandardClick={handleSelectStandard}
                pickerMode
                onQuestionFiltersChange={setFilters}
                showZeroCountStandards={showZeroCountStandards}
              />
            ) : (
              <NoSelection>
                Select a standard from the navigation tree on the left.
              </NoSelection>
            )}
          </MainScroll>

          <Footer>
            <CancelButton onClick={goBackToGroups}>Cancel</CancelButton>
            <SaveButton
              disabled={!hasChanged}
              onClick={handleSaveClick}
              aria-label="Save Standard"
            >
              Select
            </SaveButton>
          </Footer>
        </Main>
      </Body>

      <AssignGroupsDistributionModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        classroomId={classroomId}
        standardCode={selectedStandard?.code}
        standardName={selectedStandard?.name}
        totalAvailable={bundleCodeCount}
        initialCounts={initialCounts}
        hasPrerequisites={newStandardHasPrereqs}
        hasExtensions={newStandardHasExtensions}
        noQuestionTracks={noQuestionTracks}
        customizedTracks={Array.from(customizedTrackTypes(filters))}
        onSubmit={commitEdit}
        submitLabel="Save Lesson"
        onSaved={handleModalSaved}
      />

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999, maxWidth: 400 }}
        >
          {error}
        </Alert>
      )}
    </PageContainer>
  );
};

export const ViewChangeStandard = withPanelViewMemory(ViewChangeStandardPage);
